import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import clsx from 'clsx';
import { UserSearchSelect, MoveManagerDialog, type MoveOption } from '../../components/UserSearchSelect';
import SearchInput from '../../components/SearchInput';
import SearchableSelect from '../../components/SearchableSelect';
import { useTableSelection } from '../../hooks/useTableSelection';
import BulkActionBar, { type BulkAction } from '../../components/BulkActionBar';
import BulkConfirmDialog from '../../components/BulkConfirmDialog';
import { API_ORIGIN } from '../../api/baseUrl';
import {
  bulkUpdateEmployeeManager,
  bulkUpdateEmployeeTeam,
  bulkUpdateEmployeeStatus,
  bulkUpdateEmployeeLicensed,
  bulkDeleteEmployees,
  getTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  addTeamMember,
  removeTeamMember,
  getUserOwnerships,
  setUserOwnerships,
  getLevels,
  createLevel,
  updateLevel,
  deleteLevel,
  updateScopeType,
  updateScopeAll,
  getCustomersList,
  getProductsList,
  syncFromSlack,
  type Team,
  type Level,
} from '../../api/adminClient';

// Types
interface Employee {
  id: string;
  name: string;
  email: string;
  level: string;
  scope_type: 'customer' | 'product' | null;
  scope_all: boolean;
  is_active: boolean;
  is_licensed: boolean;
  slack_id: string;
  manager_id: string | null;
  manager_name?: string;
  team_name?: string;
  team_id?: string;
  created_at: string;
  updated_at: string;
}

// Level configuration
const defaultLevelConfig: Record<string, { icon: string; color: string; label: string }> = {
  ic1: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'IC 1' },
  ic2: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'IC 2' },
  staff: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'Staff' },
  sr_staff: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'Sr. Staff' },
  manager: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'Manager' },
  sr_manager: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'Sr. Manager' },
  director: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'Director' },
  vp: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'VP' },
  svp: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'SVP' },
  c_suite: { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'C-Suite' },
};
const fallbackLevelConfig = { icon: '👤', color: 'bg-gray-100 text-gray-800', label: 'Unknown' };

function getLevelConfig(level: string, levels?: Array<{ name: string; display_name: string }>): { icon: string; color: string; label: string } {
  const dbLevel = levels?.find(l => l.name === level);
  const base = defaultLevelConfig[level] || fallbackLevelConfig;
  if (dbLevel) return { ...base, label: dbLevel.display_name };
  return base;
}

// API functions
async function getEmployees(): Promise<Employee[]> {
  const response = await fetch(`${API_ORIGIN}/api/users`);
  const data = await response.json();
  return data.data || data;
}

async function updateEmployee(id: string, updates: Partial<Employee>): Promise<Employee> {
  const response = await fetch(`${API_ORIGIN}/api/users/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update employee');
  return data.data || data;
}

interface CreateEmployeeInput {
  name: string;
  email: string;
  level: string;
  manager_id?: string;
}

async function createEmployee(input: CreateEmployeeInput): Promise<Employee> {
  const response = await fetch(`${API_ORIGIN}/api/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create employee');
  return data.data || data;
}

const ITEMS_PER_PAGE = 25;

export default function AdminEmployees() {
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [teamFilter, setTeamFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [licensedFilter, setLicensedFilter] = useState<string>('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSyncOptions, setShowSyncOptions] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [bulkDialog, setBulkDialog] = useState<string | null>(null);
  const [bulkManagerId, setBulkManagerId] = useState<string | null>(null);
  const [bulkManagerChanged, setBulkManagerChanged] = useState(false);
  const [bulkTeamId, setBulkTeamId] = useState<string | null>(null);
  const [bulkTeamChanged, setBulkTeamChanged] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch employees
  const { data: employees, isLoading, error } = useQuery({
    queryKey: ['admin', 'employees'],
    queryFn: getEmployees,
  });

  // Derive selected employee from fresh query data
  const selectedEmployee = selectedEmployeeId ? employees?.find(e => e.id === selectedEmployeeId) || null : null;

  // Fetch teams
  const { data: teams } = useQuery({
    queryKey: ['admin', 'teams'],
    queryFn: () => getTeams(),
  });

  // Fetch levels
  const { data: levels } = useQuery({
    queryKey: ['admin', 'levels'],
    queryFn: getLevels,
  });

  // Update employee mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Employee> }) =>
      updateEmployee(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
      setIsEditing(false);
    },
  });

  // Sync from Slack mutation
  const syncMutation = useMutation({
    mutationFn: syncFromSlack,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
    },
  });

  // Create employee mutation
  const createMutation = useMutation({
    mutationFn: createEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
      setShowAddModal(false);
    },
  });

  // Filter employees
  const filteredEmployees = employees?.filter((emp) => {
    const matchesSearch =
      !searchTerm ||
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !levelFilter || emp.level === levelFilter;
    const matchesTeam =
      !teamFilter ||
      (teamFilter === '__none__' ? !emp.team_id : emp.team_id === teamFilter);
    const matchesStatus =
      !statusFilter ||
      (statusFilter === 'active' ? emp.is_active !== false : emp.is_active === false);
    const matchesLicensed =
      !licensedFilter ||
      (licensedFilter === 'licensed' ? emp.is_licensed === true : emp.is_licensed !== true);
    return matchesSearch && matchesRole && matchesTeam && matchesStatus && matchesLicensed;
  });

  // Table selection
  const { selectedIds, toggleOne, toggleAll, clearSelection, isAllSelected, selectionCount } =
    useTableSelection(filteredEmployees || []);

  const selectedItems = (employees || []).filter((e) => selectedIds.has(e.id));

  const handleExportCSV = () => {
    const rows = (employees || []).filter((e) => e.is_active !== false).map((e) => {
      const mgr = employees?.find((m) => m.id === e.manager_id);
      return [e.email, e.name, e.level, e.scope_type || '', String(e.is_licensed), mgr?.email || '', e.team_name || '']
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',');
    });
    const csv = ['email,name,level,scope_type,is_licensed,manager_email,team', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employees_export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const bulkActions: BulkAction[] = [
    { label: 'Change Manager', icon: '👔', onClick: () => setBulkDialog('manager') },
    { label: 'Change Team', icon: '🏠', onClick: () => setBulkDialog('team') },
    { label: 'Grant License', icon: '🔑', onClick: () => setBulkDialog('grant-license') },
    { label: 'Revoke License', icon: '🚫', onClick: () => setBulkDialog('revoke-license') },
    { label: 'Activate', onClick: () => setBulkDialog('activate') },
    { label: 'Deactivate', onClick: () => setBulkDialog('deactivate') },
    { label: 'Delete', icon: '🗑️', variant: 'danger', onClick: () => setBulkDialog('delete') },
  ];

  // Stats
  const stats = employees
    ? {
        total: employees.length,
        active: employees.filter((e) => e.is_active !== false).length,
        inactive: employees.filter((e) => e.is_active === false).length,
        licensed: employees.filter((e) => e.is_licensed === true).length,
        byLevel: Object.entries(
          employees.reduce((acc, emp) => {
            acc[emp.level] = (acc[emp.level] || 0) + 1;
            return acc;
          }, {} as Record<string, number>)
        ),
      }
    : null;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
        Failed to load employees: {(error as Error).message}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
          <p className="text-gray-500 mt-1">
            Manage employees, teams, and rollup scope assignments
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export Button */}
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <span>📤</span>
            <span>Export CSV</span>
          </button>
          {/* Import Button */}
          <button
            onClick={() => navigate('/admin/import?type=employees')}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <span>📥</span>
            <span>Import</span>
          </button>

          {/* Manage Levels Button */}
          <button
            onClick={() => { setIsEditing(false); setShowLevelModal(true); }}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <span>📊</span>
            <span>Manage Levels</span>
          </button>

          {/* Manage Teams Button */}
          <button
            onClick={() => { setIsEditing(false); setShowTeamModal(true); }}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <span>🏠</span>
            <span>Manage Teams</span>
          </button>

          {/* Sync Options Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSyncOptions(!showSyncOptions)}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <span>🔄</span>
              <span>Sync</span>
              <span className="text-xs">▼</span>
            </button>
            {showSyncOptions && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowSyncOptions(false)} />
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <button
                    onClick={() => {
                      // SB-4: prompt for a Slack bot token until per-tenant
                      // OAuth-stored credentials get wired into Command Center
                      // (SB-8). Paste a workspace-admin xoxb-… token here.
                      const token = window.prompt(
                        'Paste a Slack bot token (xoxb-…) to sync users from your workspace.',
                      );
                      if (token && token.trim().length > 0) {
                        syncMutation.mutate({ slack_token: token.trim() });
                      }
                      setShowSyncOptions(false);
                    }}
                    disabled={syncMutation.isPending}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100"
                  >
                    <span className="text-xl">💬</span>
                    <div>
                      <div className="font-medium text-gray-900">Slack</div>
                      <div className="text-xs text-gray-500">Sync from workspace</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setShowSyncOptions(false)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 opacity-60"
                  >
                    <span className="text-xl">🔐</span>
                    <div>
                      <div className="font-medium text-gray-900">Okta</div>
                      <div className="text-xs text-gray-500">Coming Soon</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setShowSyncOptions(false)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 opacity-60"
                  >
                    <span className="text-xl">🪟</span>
                    <div>
                      <div className="font-medium text-gray-900">Microsoft</div>
                      <div className="text-xs text-gray-500">Coming Soon</div>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Add Employee Button */}
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <span>+</span>
            <span>Add Employee</span>
          </button>
        </div>
      </div>

      {/* Sync Result */}
      {syncMutation.isSuccess && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
          Synced {syncMutation.data.synced} users ({syncMutation.data.added} added, {syncMutation.data.updated} updated)
        </div>
      )}
      {syncMutation.isError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {(syncMutation.error as Error).message}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer transition-all hover:ring-2 hover:ring-primary-500"
            onClick={() => { setStatusFilter(''); }}
          >
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Employees</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer transition-all hover:ring-2 hover:ring-primary-500"
            onClick={() => { setStatusFilter('active'); }}
          >
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-500">Active</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer transition-all hover:ring-2 hover:ring-primary-500"
            onClick={() => { setStatusFilter('inactive'); }}
          >
            <div className="text-2xl font-bold text-red-500">{stats.inactive}</div>
            <div className="text-sm text-gray-500">Inactive</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer transition-all hover:ring-2 hover:ring-primary-500"
            onClick={() => { setLicensedFilter(licensedFilter === 'licensed' ? '' : 'licensed'); }}
          >
            <div className="text-2xl font-bold text-blue-600">{stats.licensed}</div>
            <div className="text-sm text-gray-500">Licensed</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <SearchInput
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setVisibleCount(ITEMS_PER_PAGE); }}
              placeholder="Search by name or email..."
            />
          </div>
          <SearchableSelect
            value={levelFilter}
            onChange={(v) => { setLevelFilter(v); setVisibleCount(ITEMS_PER_PAGE); }}
            options={[
              { value: '', label: 'All Levels' },
              ...(levels || []).map((l) => ({ value: l.name, label: l.display_name })),
            ]}
            placeholder="All Levels"
            searchPlaceholder="Filter levels..."
            className="w-48"
          />
          <SearchableSelect
            value={teamFilter}
            onChange={(v) => { setTeamFilter(v); setVisibleCount(ITEMS_PER_PAGE); }}
            options={[
              { value: '', label: 'All Teams' },
              { value: '__none__', label: 'No Team' },
              ...(teams || []).map((t) => ({ value: t.id, label: t.name })),
            ]}
            placeholder="All Teams"
            searchPlaceholder="Filter teams..."
            className="w-48"
          />
          <SearchableSelect
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setVisibleCount(ITEMS_PER_PAGE); }}
            options={[
              { value: '', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            placeholder="All Status"
            searchPlaceholder="Filter status..."
            className="w-48"
          />
          <SearchableSelect
            value={licensedFilter}
            onChange={(v) => { setLicensedFilter(v); setVisibleCount(ITEMS_PER_PAGE); }}
            options={[
              { value: '', label: 'All Licensed' },
              { value: 'licensed', label: 'Licensed' },
              { value: 'unlicensed', label: 'Unlicensed' },
            ]}
            placeholder="All Licensed"
            searchPlaceholder="Filter license..."
            className="w-48"
          />
          {(levelFilter || teamFilter || statusFilter || licensedFilter) && (
            <button
              onClick={() => { setLevelFilter(''); setTeamFilter(''); setStatusFilter('active'); setLicensedFilter(''); }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex gap-6">
        {/* Employee List */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : !filteredEmployees || filteredEmployees.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              {searchTerm || levelFilter || teamFilter || statusFilter || licensedFilter ? 'No employees match your filters' : 'No employees found'}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleAll}
                      className="rounded border-gray-300"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manager
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEmployees.slice(0, visibleCount).map((employee) => {
                  const config = getLevelConfig(employee.level, levels);
                  const manager = employees?.find((e) => e.id === employee.manager_id);
                  return (
                    <tr
                      key={employee.id}
                      className={clsx(
                        'cursor-pointer transition-colors',
                        selectedEmployee?.id === employee.id
                          ? 'bg-primary-50'
                          : 'hover:bg-gray-50'
                      )}
                      onClick={() => {
                        setSelectedEmployeeId(employee.id);
                        setIsEditing(false);
                      }}
                    >
                      <td className="px-3 py-4 w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(employee.id)}
                          onChange={() => toggleOne(employee.id)}
                          className="rounded border-gray-300"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className={clsx('flex items-center gap-3', !employee.is_active && 'opacity-50')}>
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-lg">
                            {config.icon}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-2">
                              {employee.name}
                              {!employee.is_active && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-600">Inactive</span>
                              )}
                              {employee.is_licensed && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-600">Licensed</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{employee.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', config.color)}>
                          {config.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {employee.team_name ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            {employee.team_name}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 italic">No team</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {manager ? manager.name : <span className="italic">None</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {format(new Date(employee.created_at), 'MMM d, yyyy')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Show More Button */}
          {filteredEmployees && filteredEmployees.length > visibleCount && (
            <div className="p-4 border-t border-gray-100 text-center">
              <button
                onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_PAGE)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Show More ({filteredEmployees.length - visibleCount} remaining)
              </button>
            </div>
          )}

          {/* Showing count */}
          {filteredEmployees && filteredEmployees.length > 0 && (
            <div className="p-3 bg-gray-50 border-t border-gray-100 text-center text-sm text-gray-500">
              Showing {Math.min(visibleCount, filteredEmployees.length)} of {filteredEmployees.length} employees
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedEmployee && (
          <EmployeeDetailPanel
            employee={selectedEmployee}
            employees={employees || []}
            teams={teams || []}
            levels={levels}
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            onCancel={() => setIsEditing(false)}
            onClose={() => {
              setSelectedEmployeeId(null);
              setIsEditing(false);
            }}
            onSave={(updates) => {
              updateMutation.mutate({ id: selectedEmployee.id, updates });
            }}
            isSaving={updateMutation.isPending}
            onRefresh={() => {
              queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
            }}
          />
        )}
      </div>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectionCount={selectionCount}
        actions={bulkActions}
        onClear={clearSelection}
      />

      {/* Bulk Confirm Dialogs */}
      {bulkDialog === 'manager' && (
        <BulkConfirmDialog
          title="Change Manager"
          description="Update the manager for selected employees"
          items={selectedItems.map((e) => ({ id: e.id, label: e.name }))}
          confirmLabel="Update Manager"
          confirmDisabled={!bulkManagerChanged}
          onConfirm={async () => {
            const res = await bulkUpdateEmployeeManager(
              selectedItems.map((e) => e.id),
              bulkManagerId
            );
            queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
            setBulkManagerId(null);
            setBulkManagerChanged(false);
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => { setBulkDialog(null); setBulkManagerId(null); setBulkManagerChanged(false); }}
        >
          <UserSearchSelect
            users={(employees || []).filter((e) => !selectedIds.has(e.id)).map(e => ({ ...e, role: e.level }))}
            selectedId={bulkManagerId}
            onChange={(id) => { setBulkManagerId(id); setBulkManagerChanged(true); }}
            roleConfig={defaultLevelConfig}
            placeholder="Search for new manager..."
            allowEmpty={true}
            emptyLabel="No Manager (Top Level)"
          />
        </BulkConfirmDialog>
      )}

      {bulkDialog === 'team' && (
        <BulkConfirmDialog
          title="Change Team"
          description="Update the team for selected employees"
          items={selectedItems.map((e) => ({ id: e.id, label: e.name }))}
          confirmLabel="Update Team"
          confirmDisabled={!bulkTeamChanged}
          onConfirm={async () => {
            const res = await bulkUpdateEmployeeTeam(
              selectedItems.map((e) => e.id),
              bulkTeamId
            );
            queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
            setBulkTeamId(null);
            setBulkTeamChanged(false);
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => { setBulkDialog(null); setBulkTeamId(null); setBulkTeamChanged(false); }}
        >
          <select
            value={bulkTeamId || ''}
            onChange={(e) => { setBulkTeamId(e.target.value || null); setBulkTeamChanged(true); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
          >
            <option value="">No Team</option>
            {(teams || []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </BulkConfirmDialog>
      )}

      {bulkDialog === 'activate' && (
        <BulkConfirmDialog
          title="Activate Employees"
          description="Set selected employees to active status"
          items={selectedItems.map((e) => ({ id: e.id, label: e.name }))}
          confirmLabel="Activate"
          onConfirm={async () => {
            const res = await bulkUpdateEmployeeStatus(selectedItems.map((e) => e.id), true);
            queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {bulkDialog === 'deactivate' && (
        <BulkConfirmDialog
          title="Deactivate Employees"
          description="Set selected employees to inactive status"
          items={selectedItems.map((e) => ({ id: e.id, label: e.name }))}
          confirmLabel="Deactivate"
          variant="danger"
          onConfirm={async () => {
            const res = await bulkUpdateEmployeeStatus(selectedItems.map((e) => e.id), false);
            queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {bulkDialog === 'grant-license' && (
        <BulkConfirmDialog
          title="Grant License"
          description="Grant PowerMove license to selected employees"
          items={selectedItems.map((e) => ({ id: e.id, label: e.name }))}
          confirmLabel="Grant License"
          onConfirm={async () => {
            const res = await bulkUpdateEmployeeLicensed(selectedItems.map((e) => e.id), true);
            queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {bulkDialog === 'revoke-license' && (
        <BulkConfirmDialog
          title="Revoke License"
          description="Revoke PowerMove license from selected employees"
          items={selectedItems.map((e) => ({ id: e.id, label: e.name }))}
          confirmLabel="Revoke License"
          variant="danger"
          onConfirm={async () => {
            const res = await bulkUpdateEmployeeLicensed(selectedItems.map((e) => e.id), false);
            queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {bulkDialog === 'delete' && (
        <BulkConfirmDialog
          title="Delete Employees"
          description="This will permanently remove the selected employees"
          items={selectedItems.map((e) => ({ id: e.id, label: e.name }))}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={async () => {
            const res = await bulkDeleteEmployees(selectedItems.map((e) => e.id));
            queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <AddEmployeeModal
          employees={employees || []}
          levels={levels}
          onClose={() => setShowAddModal(false)}
          onSave={(input) => createMutation.mutate(input)}
          isSaving={createMutation.isPending}
          error={createMutation.isError ? (createMutation.error as Error).message : null}
        />
      )}

      {/* Team Management Modal */}
      {showTeamModal && (
        <TeamManagementModal
          onClose={() => {
            setShowTeamModal(false);
            queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
          }}
        />
      )}

      {/* Level Management Modal */}
      {showLevelModal && (
        <LevelManagementModal
          onClose={() => {
            setShowLevelModal(false);
            queryClient.invalidateQueries({ queryKey: ['admin', 'levels'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
          }}
        />
      )}
    </div>
  );
}

// Search level config for UserSearchSelect component
const searchLevelConfig = defaultLevelConfig;

interface EmployeeDetailPanelProps {
  employee: Employee;
  employees: Employee[];
  teams: Team[];
  levels?: Level[];
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onClose: () => void;
  onSave: (updates: Partial<Employee>) => void;
  isSaving: boolean;
  onRefresh: () => void;
}

function EmployeeDetailPanel({
  employee,
  employees,
  teams,
  levels,
  isEditing,
  onEdit,
  onCancel,
  onClose,
  onSave,
  isSaving,
  onRefresh,
}: EmployeeDetailPanelProps) {
  const queryClient = useQueryClient();
  const [editForm, setEditForm] = useState({
    name: employee.name,
    email: employee.email,
    level: employee.level,
  });
  const [selectedManagerId, setSelectedManagerId] = useState<string | null>(employee.manager_id || null);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(employee.team_id || '');
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [pendingNewManagerId, setPendingNewManagerId] = useState<string | null>(null);

  // Rollup scope state
  const [scopeCustomers, setScopeCustomers] = useState<string[]>([]);
  const [scopeProducts, setScopeProducts] = useState<string[]>([]);
  const [scopeAll, setScopeAll] = useState(false);
  const [originalScope, setOriginalScope] = useState<{ customers: string[]; products: string[]; scopeAll: boolean }>({ customers: [], products: [], scopeAll: false });

  // Fetch ownerships for this employee
  const { data: ownerships } = useQuery({
    queryKey: ['admin', 'ownerships', employee.id],
    queryFn: () => getUserOwnerships(employee.id),
  });

  // Fetch all customers and products for scope selection
  const { data: allCustomers } = useQuery({
    queryKey: ['customers-list'],
    queryFn: getCustomersList,
  });

  const { data: allProducts } = useQuery({
    queryKey: ['products-list'],
    queryFn: getProductsList,
  });

  // Reset form when employee changes
  // Reset form when a different employee is selected OR when entering edit mode
  useEffect(() => {
    setEditForm({
      name: employee.name,
      email: employee.email,
      level: employee.level,
    });
    setSelectedManagerId(employee.manager_id || null);
    setSelectedTeamId(employee.team_id || '');
    setShowMoveDialog(false);
  }, [employee.id, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set scope from ownerships when loaded
  useEffect(() => {
    if (ownerships) {
      const cIds = ownerships.filter(o => o.entity_type === 'customer').map(o => o.entity_id);
      const pIds = ownerships.filter(o => o.entity_type === 'product').map(o => o.entity_id);
      setScopeCustomers(cIds);
      setScopeProducts(pIds);
      setScopeAll(employee.scope_all || false);
      setOriginalScope({ customers: cIds, products: pIds, scopeAll: employee.scope_all || false });
    }
  }, [ownerships, employee.scope_all]);

  const config = getLevelConfig(employee.level, levels);
  const manager = employees.find((e) => e.id === employee.manager_id);
  const directReports = employees.filter((e) => e.manager_id === employee.id);

  // Get potential managers (exclude self and descendants)
  const getDescendantIds = (userId: string): Set<string> => {
    const ids = new Set<string>();
    const children = employees.filter((e) => e.manager_id === userId);
    children.forEach((child) => {
      ids.add(child.id);
      getDescendantIds(child.id).forEach((id) => ids.add(id));
    });
    return ids;
  };
  const descendantIds = getDescendantIds(employee.id);
  const potentialManagers = employees.filter(
    (e) => e.id !== employee.id && !descendantIds.has(e.id)
  );

  // Move mutation (with team options)
  const moveMutation = useMutation({
    mutationFn: async ({ newManagerId, moveOption }: { newManagerId: string | null; moveOption: MoveOption }) => {
      const response = await fetch(`${API_ORIGIN}/api/users/${employee.id}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_manager_id: newManagerId, move_option: moveOption }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to move user');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
      setShowMoveDialog(false);
      onRefresh();
    },
  });

  // Simple manager update mutation
  const simpleManagerMutation = useMutation({
    mutationFn: async (managerId: string | null) => {
      const response = await fetch(`${API_ORIGIN}/api/users/${employee.id}/manager`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manager_id: managerId }),
      });
      if (!response.ok) throw new Error('Failed to update manager');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
      onRefresh();
    },
  });

  // Team change mutation
  const teamChangeMutation = useMutation({
    mutationFn: async (newTeamId: string) => {
      // Remove from current team
      if (employee.team_id) {
        await removeTeamMember(employee.team_id, employee.id);
      }
      // Add to new team
      if (newTeamId) {
        await addTeamMember(newTeamId, employee.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
      onRefresh();
    },
  });

  // Save scope mutation
  const saveScopeMutation = useMutation({
    mutationFn: async () => {
      // Save scope_all flag
      if (scopeAll !== originalScope.scopeAll) {
        await updateScopeAll(employee.id, scopeAll);
      }
      // Save individual ownerships (only meaningful when scope_all is false)
      const ownerships = [
        ...scopeCustomers.map(id => ({ entity_type: 'customer', entity_id: id })),
        ...scopeProducts.map(id => ({ entity_type: 'product', entity_id: id })),
      ];
      return setUserOwnerships(employee.id, ownerships);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ownerships', employee.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
    },
  });

  const handleSaveDetails = async () => {
    // Save name/email/level
    onSave(editForm);
    // Save team change if needed
    const hasTeamChange = selectedTeamId !== (employee.team_id || '');
    if (hasTeamChange) {
      teamChangeMutation.mutate(selectedTeamId);
    }
  };

  const handleManagerChange = (newManagerId: string | null) => {
    setSelectedManagerId(newManagerId);
  };

  const handleManagerSave = () => {
    const newManagerId = selectedManagerId;
    if (directReports.length > 0 && newManagerId !== (employee.manager_id || null)) {
      setPendingNewManagerId(newManagerId);
      setShowMoveDialog(true);
    } else {
      simpleManagerMutation.mutate(newManagerId);
    }
  };

  const handleMoveConfirm = (moveOption: MoveOption) => {
    moveMutation.mutate({ newManagerId: pendingNewManagerId, moveOption });
  };

  const handleTeamChange = (teamId: string) => {
    setSelectedTeamId(teamId);
  };

  const hasManagerChange = selectedManagerId !== (employee.manager_id || null);
  const hasScopeChanges =
    JSON.stringify([...scopeCustomers].sort()) !== JSON.stringify([...originalScope.customers].sort()) ||
    JSON.stringify([...scopeProducts].sort()) !== JSON.stringify([...originalScope.products].sort()) ||
    scopeAll !== originalScope.scopeAll;

  return (
    <>
      <div className="w-96 flex-shrink-0 sticky top-8 self-start">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={clsx('w-16 h-16 rounded-full flex items-center justify-center text-3xl border-2', config.color)}>
                {config.icon}
              </div>
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="text-xl font-bold text-gray-900 border border-gray-300 rounded-lg px-2 py-1 w-full focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  />
                ) : (
                  <h2 className="text-xl font-bold text-gray-900">{employee.name}</h2>
                )}
                {isEditing ? (
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="text-gray-500 border border-gray-300 rounded-lg px-2 py-1 w-full mt-1 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  />
                ) : (
                  <p className="text-gray-500 text-sm">{employee.email}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>

          {/* Level */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Level</h3>
            {isEditing ? (
              <select
                value={editForm.level}
                onChange={(e) => setEditForm({ ...editForm, level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              >
                {/* Fallback option if current value isn't in the levels list */}
                {!(levels || []).some(l => l.name === editForm.level) && (
                  <option value={editForm.level} disabled>
                    {editForm.level} (not in levels)
                  </option>
                )}
                {(levels || []).map((lvl) => {
                  const lvlConfig = getLevelConfig(lvl.name, levels);
                  return (
                    <option key={lvl.name} value={lvl.name}>
                      {lvlConfig.icon} {lvl.display_name}
                    </option>
                  );
                })}
              </select>
            ) : (
              <span className={clsx('inline-flex items-center px-3 py-1 rounded-full text-sm font-medium', config.color)}>
                {config.icon} {config.label}
              </span>
            )}
          </div>

          {/* Team */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Team</h3>
            {isEditing ? (
              <>
                <select
                  value={selectedTeamId}
                  onChange={(e) => handleTeamChange(e.target.value)}
                  disabled={teamChangeMutation.isPending}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none disabled:opacity-50"
                >
                  <option value="">No Team</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {teamChangeMutation.isPending && (
                  <p className="text-xs text-gray-400 mt-1">Updating team...</p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-900">{employee.team_name || 'No Team'}</p>
            )}
          </div>

          {/* Reports To */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Reports To</h3>
            {isEditing ? (
              <>
                <UserSearchSelect
                  users={potentialManagers.map(e => ({ ...e, role: e.level }))}
                  selectedId={selectedManagerId}
                  onChange={handleManagerChange}
                  roleConfig={searchLevelConfig}
                  placeholder="Search by name or email..."
                  allowEmpty={true}
                  emptyLabel="No Manager (Top Level)"
                />
                {hasManagerChange && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={handleManagerSave}
                      disabled={simpleManagerMutation.isPending || moveMutation.isPending}
                      className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
                    >
                      {simpleManagerMutation.isPending || moveMutation.isPending ? 'Saving...' : 'Save Manager Change'}
                    </button>
                    <button
                      onClick={() => setSelectedManagerId(employee.manager_id || null)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                    >
                      Reset
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-900">{manager ? manager.name : 'No Manager (Top Level)'}</p>
            )}
          </div>

          {/* Direct Reports */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Direct Reports ({directReports.length})
            </h3>
            {directReports.length > 0 ? (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {directReports.map((report) => {
                  const reportConfig = getLevelConfig(report.level, levels);
                  return (
                    <div key={report.id} className={clsx('flex items-center gap-2 px-3 py-2 rounded-lg border', reportConfig.color)}>
                      <span>{reportConfig.icon}</span>
                      <span className="text-sm font-medium">{report.name}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic py-2">No direct reports</p>
            )}
          </div>

          {/* Report Scope */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Report Scope</h3>
            {isEditing ? (
              <>
                <p className="text-xs text-gray-500 mb-3">
                  Determines what type of scope-based reports this employee receives.
                </p>
                <select
                  value={employee.scope_type || ''}
                  onChange={async (e) => {
                    const val = e.target.value as 'customer' | 'product' | '';
                    await updateScopeType(employee.id, val || null);
                    onRefresh();
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                >
                  <option value="">None</option>
                  <option value="customer">Customer-based</option>
                  <option value="product">Product-based</option>
                </select>
                {employee.scope_type === 'customer' && (
                  <p className="text-xs text-gray-400 mt-1">Assign customers in the Rollup Scope section below.</p>
                )}
                {employee.scope_type === 'product' && (
                  <p className="text-xs text-gray-400 mt-1">Assign products in the Rollup Scope section below.</p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-900">
                {employee.scope_type === 'customer' ? 'Customer-based' : employee.scope_type === 'product' ? 'Product-based' : 'None'}
              </p>
            )}
          </div>

          {/* Rollup Scope */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Rollup Scope</h3>
            {isEditing ? (
              <>
                <p className="text-xs text-gray-500 mb-3">
                  Assign which customers and products this employee receives rollups for.
                </p>

                {/* All scope toggle */}
                {employee.scope_type && (
                  <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scopeAll}
                        onChange={(e) => setScopeAll(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {employee.scope_type === 'customer' ? 'All Customers' : 'All Products'}
                      </span>
                      <span className="text-xs text-gray-400">
                        — receives reports for {employee.scope_type === 'customer' ? 'every customer' : 'every product'}
                      </span>
                    </label>
                  </div>
                )}

                {/* Assigned Customers */}
                <div className="mb-3">
                  <h4 className="text-xs font-medium text-gray-600 mb-1">Assigned Customers</h4>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {scopeCustomers.map((cId) => {
                      const cust = allCustomers?.find(c => c.id === cId);
                      return (
                        <span key={cId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                          {cust?.name || cId}
                          <button
                            onClick={() => setScopeCustomers(prev => prev.filter(id => id !== cId))}
                            className="hover:text-blue-600"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    {scopeCustomers.length === 0 && (
                      <span className="text-xs text-gray-400 italic">None assigned</span>
                    )}
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !scopeCustomers.includes(e.target.value)) {
                        setScopeCustomers(prev => [...prev, e.target.value]);
                      }
                    }}
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-primary-500 outline-none"
                  >
                    <option value="">+ Add customer...</option>
                    {(allCustomers || [])
                      .filter(c => !scopeCustomers.includes(c.id))
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                  </select>
                </div>

                {/* Assigned Products */}
                <div className="mb-3">
                  <h4 className="text-xs font-medium text-gray-600 mb-1">Assigned Products</h4>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {scopeProducts.map((pId) => {
                      const prod = allProducts?.find(p => p.id === pId);
                      return (
                        <span key={pId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                          {prod?.name || pId}
                          <button
                            onClick={() => setScopeProducts(prev => prev.filter(id => id !== pId))}
                            className="hover:text-green-600"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                    {scopeProducts.length === 0 && (
                      <span className="text-xs text-gray-400 italic">None assigned</span>
                    )}
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !scopeProducts.includes(e.target.value)) {
                        setScopeProducts(prev => [...prev, e.target.value]);
                      }
                    }}
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-primary-500 outline-none"
                  >
                    <option value="">+ Add product...</option>
                    {(allProducts || [])
                      .filter(p => !scopeProducts.includes(p.id))
                      .map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                  </select>
                </div>

                {/* Scope save/reset */}
                {hasScopeChanges && (
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => saveScopeMutation.mutate()}
                      disabled={saveScopeMutation.isPending}
                      className="flex-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs hover:bg-primary-700 disabled:opacity-50"
                    >
                      {saveScopeMutation.isPending ? 'Saving...' : 'Save Scope'}
                    </button>
                    <button
                      onClick={() => {
                        setScopeCustomers(originalScope.customers);
                        setScopeProducts(originalScope.products);
                        setScopeAll(originalScope.scopeAll);
                      }}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200"
                    >
                      Reset
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {scopeAll && employee.scope_type && (
                  <div className="mb-2">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 font-medium">
                      {employee.scope_type === 'customer' ? 'All Customers' : 'All Products'}
                    </span>
                  </div>
                )}
                {!scopeAll && scopeCustomers.length > 0 && (
                  <div className="mb-2">
                    <h4 className="text-xs font-medium text-gray-600 mb-1">Customers</h4>
                    <div className="flex flex-wrap gap-1">
                      {scopeCustomers.map((cId) => {
                        const cust = allCustomers?.find(c => c.id === cId);
                        return (
                          <span key={cId} className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                            {cust?.name || cId}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                {!scopeAll && scopeProducts.length > 0 && (
                  <div className="mb-2">
                    <h4 className="text-xs font-medium text-gray-600 mb-1">Products</h4>
                    <div className="flex flex-wrap gap-1">
                      {scopeProducts.map((pId) => {
                        const prod = allProducts?.find(p => p.id === pId);
                        return (
                          <span key={pId} className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                            {prod?.name || pId}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                {scopeCustomers.length === 0 && scopeProducts.length === 0 && (
                  <p className="text-sm text-gray-400 italic">None assigned</p>
                )}
              </>
            )}
          </div>

          {/* Licensed Status */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">License</h3>
            {isEditing ? (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={employee.is_licensed}
                  onChange={async (e) => {
                    const response = await fetch(`${API_ORIGIN}/api/users/${employee.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ is_licensed: e.target.checked }),
                    });
                    if (response.ok) onRefresh();
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">PowerMove Licensed</span>
              </label>
            ) : (
              <div className="flex items-center gap-2">
                {employee.is_licensed ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Licensed
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    Unlicensed
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Slack Info */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Slack</h3>
            <p className="text-sm font-mono text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
              {employee.slack_id || 'Not linked'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-gray-100">
            {isEditing ? (
              <>
                <button
                  onClick={handleSaveDetails}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={onCancel}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={onEdit}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Edit Details
              </button>
            )}
          </div>

          {/* Metadata */}
          <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
            <p>Created: {format(new Date(employee.created_at), 'MMM d, yyyy h:mm a')}</p>
            <p>Updated: {format(new Date(employee.updated_at), 'MMM d, yyyy h:mm a')}</p>
          </div>
        </div>
      </div>

      {/* Move Manager Dialog */}
      {showMoveDialog && (
        <MoveManagerDialog
          employee={{ id: employee.id, name: employee.name, email: employee.email, role: employee.level }}
          directReports={directReports.map(r => ({ id: r.id, name: r.name, email: r.email, role: r.level }))}
          oldManager={manager ? { id: manager.id, name: manager.name, email: manager.email, role: manager.level } : null}
          newManager={pendingNewManagerId ? (() => {
            const m = employees.find(e => e.id === pendingNewManagerId);
            return m ? { id: m.id, name: m.name, email: m.email, role: m.level } : null;
          })() : null}
          roleConfig={searchLevelConfig}
          onConfirm={handleMoveConfirm}
          onCancel={() => setShowMoveDialog(false)}
          isLoading={moveMutation.isPending}
        />
      )}
    </>
  );
}

// ============================================================================
// ADD EMPLOYEE MODAL
// ============================================================================

interface AddEmployeeModalProps {
  employees: Employee[];
  levels?: Level[];
  onClose: () => void;
  onSave: (input: CreateEmployeeInput) => void;
  isSaving: boolean;
  error: string | null;
}

function AddEmployeeModal({ employees, levels, onClose, onSave, isSaving, error }: AddEmployeeModalProps) {
  const [form, setForm] = useState<CreateEmployeeInput>({
    name: '',
    email: '',
    level: 'ic1',
    manager_id: undefined,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Add Employee</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 text-xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              placeholder="Jane Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              placeholder="jane@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Level <span className="text-red-500">*</span>
            </label>
            <select
              value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            >
              {(levels || []).map((l) => {
                const lvlConfig = getLevelConfig(l.name, levels);
                return (
                  <option key={l.name} value={l.name}>
                    {lvlConfig.icon} {l.display_name}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reports To
            </label>
            <select
              value={form.manager_id || ''}
              onChange={(e) => setForm({ ...form, manager_id: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            >
              <option value="">No Manager (Top Level)</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} ({getLevelConfig(emp.level, levels).label})
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isSaving || !form.name || !form.email}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Adding...' : 'Add Employee'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// TEAM MANAGEMENT MODAL
// ============================================================================

interface TeamManagementModalProps {
  onClose: () => void;
}

function TeamManagementModal({ onClose }: TeamManagementModalProps) {
  const queryClient = useQueryClient();
  const [newTeamName, setNewTeamName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const { data: teams, isLoading } = useQuery({
    queryKey: ['admin', 'teams'],
    queryFn: () => getTeams(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => createTeam({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
      setNewTeamName('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateTeam(id, { name } as Partial<Team>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
      setEditingId(null);
      setEditingName('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });

  const handleAddTeam = () => {
    if (!newTeamName.trim()) return;
    if ((teams || []).some(t => t.name.toLowerCase() === newTeamName.trim().toLowerCase())) {
      alert('A team with this name already exists');
      return;
    }
    createMutation.mutate(newTeamName.trim());
  };

  const handleSaveEdit = () => {
    if (!editingId || !editingName.trim()) return;
    if ((teams || []).some(t => t.id !== editingId && t.name.toLowerCase() === editingName.trim().toLowerCase())) {
      alert('A team with this name already exists');
      return;
    }
    updateMutation.mutate({ id: editingId, name: editingName.trim() });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Manage Teams</h2>
            <p className="text-sm text-gray-500">Create, rename, and delete teams</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Add New Team */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Add New Team</h3>
            <div className="flex gap-3">
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Team name (e.g. Platform, Growth, Sales)..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                onKeyDown={(e) => e.key === 'Enter' && handleAddTeam()}
              />
              <button
                onClick={handleAddTeam}
                disabled={!newTeamName.trim() || createMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isPending ? 'Adding...' : 'Add'}
              </button>
            </div>
            {createMutation.isError && (
              <p className="mt-2 text-sm text-red-600">
                {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create team'}
              </p>
            )}
          </div>

          {/* Team List */}
          <div className="space-y-2">
            {isLoading ? (
              <div className="text-center text-gray-500 py-8">Loading teams...</div>
            ) : !teams || teams.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No teams yet. Add your first team above.
              </div>
            ) : (
              teams.map((team) => (
                <div
                  key={team.id}
                  className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                >
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-800">
                    🏠
                  </span>
                  {editingId === team.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit();
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{team.name}</span>
                      {team.member_count !== undefined && (
                        <span className="ml-2 text-xs text-gray-400">
                          ({team.member_count} members)
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    {editingId === team.id ? (
                      <>
                        <button
                          onClick={handleSaveEdit}
                          disabled={updateMutation.isPending}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setEditingId(team.id);
                            setEditingName(team.name);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Rename"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Delete team "${team.name}"? Members will be unassigned.`)) {
                              deleteMutation.mutate(team.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// LEVEL MANAGEMENT MODAL
// ============================================================================

interface LevelManagementModalProps {
  onClose: () => void;
}

function LevelManagementModal({ onClose }: LevelManagementModalProps) {
  const queryClient = useQueryClient();
  const [newLevelDisplayName, setNewLevelDisplayName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDisplayName, setEditingDisplayName] = useState('');

  const slugify = (name: string) =>
    name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const { data: levelsList, isLoading } = useQuery({
    queryKey: ['admin', 'levels'],
    queryFn: getLevels,
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; display_name: string }) => createLevel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'levels'] });
      setNewLevelDisplayName('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, display_name }: { id: string; display_name: string }) => updateLevel(id, { display_name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'levels'] });
      setEditingId(null);
      setEditingDisplayName('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteLevel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'levels'] });
    },
  });

  const handleAddLevel = () => {
    const displayName = newLevelDisplayName.trim();
    if (!displayName) return;
    const key = slugify(displayName);
    if (!key) return;
    if ((levelsList || []).some(l => l.name === key)) {
      alert(`A level with key "${key}" already exists`);
      return;
    }
    createMutation.mutate({ name: key, display_name: displayName });
  };

  const handleSaveEdit = () => {
    if (!editingId || !editingDisplayName.trim()) return;
    updateMutation.mutate({ id: editingId, display_name: editingDisplayName.trim() });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Manage Levels</h2>
            <p className="text-sm text-gray-500">Create, rename, and delete employee levels</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Add New Level */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Add New Level</h3>
            <div className="flex gap-3">
              <input
                type="text"
                value={newLevelDisplayName}
                onChange={(e) => setNewLevelDisplayName(e.target.value)}
                placeholder="Level name (e.g. Sr. Engineer)..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                onKeyDown={(e) => e.key === 'Enter' && handleAddLevel()}
              />
              <button
                onClick={handleAddLevel}
                disabled={!newLevelDisplayName.trim() || createMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isPending ? 'Adding...' : 'Add'}
              </button>
            </div>
            {createMutation.isError && (
              <p className="mt-2 text-sm text-red-600">
                {(createMutation.error as Error).message || 'Failed to create level'}
              </p>
            )}
          </div>

          {/* Level List */}
          <div className="space-y-2">
            {isLoading ? (
              <div className="text-center text-gray-500 py-8">Loading levels...</div>
            ) : !levelsList || levelsList.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No levels yet. Add your first level above.
              </div>
            ) : (
              [...levelsList].sort((a, b) => a.display_name.localeCompare(b.display_name)).map((lvl) => {
                const lvlConfig = getLevelConfig(lvl.name);
                return (
                  <div
                    key={lvl.id}
                    className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  >
                    <span className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', lvlConfig.color)}>
                      {lvlConfig.icon}
                    </span>
                    {editingId === lvl.id ? (
                      <input
                        type="text"
                        value={editingDisplayName}
                        onChange={(e) => setEditingDisplayName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit();
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                    ) : (
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{lvl.display_name}</span>
                        <span className="ml-2 text-xs text-gray-400 font-mono">({lvl.name})</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      {editingId === lvl.id ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isPending}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setEditingId(lvl.id);
                              setEditingDisplayName(lvl.display_name);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            title="Rename"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Delete level "${lvl.display_name}"? Employees with this level will need to be reassigned.`)) {
                                deleteMutation.mutate(lvl.id);
                              }
                            }}
                            disabled={deleteMutation.isPending}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
